import { BaseEdge, type EdgeProps, type Edge as FlowEdge, getStraightPath, useInternalNode } from "@xyflow/react";
import React from "react";

import { getFloatingEdgeParams } from "./floatingEdge.utils";

import styles from "./FloatingEdge.module.css";

/** `derived` marks a connector the canvas inferred (uncle/aunt, cousin); the canvas gates its visibility. */
type FloatingEdgeData = { variant: "hierarchical" | "thread" | "spoke"; tooltip?: string; derived?: boolean };

const LINE_CLASSES: Record<FloatingEdgeData["variant"], string> = {
	hierarchical: styles.hierarchicalLine,
	thread: styles.threadLine,
	spoke: styles.spokeLine
};

/**
 * Plain floating connector: solid for parent/child edges, dashed for generic threads, and a faint
 * spoke from a selected node out to each chip in its ring.
 */
const FloatingEdge = ({ data, id, source, target }: EdgeProps<FlowEdge<FloatingEdgeData>>) => {
	const sourceNode = useInternalNode(source);
	const targetNode = useInternalNode(target);

	if (!sourceNode || !targetNode) {
		return null;
	}

	const { sx, sy, tx, ty } = getFloatingEdgeParams(sourceNode, targetNode);
	const [path] = getStraightPath({ sourceX: sx, sourceY: sy, targetX: tx, targetY: ty });
	const className = LINE_CLASSES[data?.variant ?? "thread"];

	return (
		<>
			<BaseEdge className={className} id={id} path={path} />
			{data?.tooltip && (
				<path className={styles.hoverPath} d={path}>
					<title>{data.tooltip}</title>
				</path>
			)}
		</>
	);
};

export default FloatingEdge;
